/**
 * @fileoverview Um link do quadro na lista: nome, situação (ativo, expirado,
 * revogado), validade, senha, o que mostra, quem criou e o último acesso. Link
 * ativo copia de novo e tem as ações (senha, validade, link novo, revogar);
 * expirado ou revogado é só histórico.
 */

import React, { useState } from "react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Copy, Check, MoreHorizontal, KeyRound, CalendarClock, RefreshCw, Ban } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { formatDateTime } from "../../utils/formatLocaleDate";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`boardShare.row.${key}`, options);

const STATE_CLASS = { active: "stateActive", expired: "stateExpired", revoked: "stateRevoked" };

const validityOf = (share) => {
  if (share.state === "revoked") {
    const date = formatDateTime(share.revokedAt);
    return share.revokedBy ? t("revokedBy", { date, name: share.revokedBy }) : t("revokedOn", { date });
  }
  if (!share.expiresAt) return t("permanent");
  const date = formatDateTime(share.expiresAt);
  return share.state === "expired" ? t("expiredOn", { date }) : t("until", { date });
};

/** As linhas de baixo do link: validade, proteção, o que mostra; quem criou e o último acesso. */
export const shareFacts = (share) => {
  const validity = validityOf(share);
  const shows = [share.showContactInfo && t("showsContact"), share.showCardDetails && t("showsDetails")].filter(Boolean);
  const first = [validity, share.passwordProtected ? t("withPassword") : t("withoutPassword"), ...shows].join(" · ");
  const created = share.createdBy
    ? t("createdBy", { date: formatDateTime(share.createdAt), name: share.createdBy })
    : t("createdAt", { date: formatDateTime(share.createdAt) });
  const access = share.lastAccessAt ? t("lastAccess", { date: formatDateTime(share.lastAccessAt) }) : t("neverAccessed");
  return [first, `${created} · ${access}`];
};

const ShareRow = ({ share, busy, copied, onCopy, onPassword, onValidity, onRenew, onRevoke }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const active = share.state === "active";
  const [first, second] = shareFacts(share);

  const pick = (action) => () => {
    setAnchor(null);
    action(share);
  };

  return (
    <div className={`${classes.row} ${active ? "" : classes.rowInactive}`} data-share-row={share.id}>
      <div className={classes.rowMain}>
        <div className={classes.rowTitle}>
          <span className={classes.rowName} title={share.name}>
            {share.name}
          </span>
          <span className={`${classes.stateChip} ${classes[STATE_CLASS[share.state]]}`}>{t(share.state)}</span>
        </div>
        <span className={classes.rowMeta}>{first}</span>
        <span className={classes.rowMeta}>{second}</span>
      </div>
      {active && (
        <div className={classes.rowActions}>
          <Tooltip title={t("copyLink")}>
            <span>
              <button
                type="button"
                className={`${classes.iconBtn} ${copied ? classes.copied : ""}`}
                onClick={() => onCopy(share)}
                disabled={busy}
                aria-label={t("copyLink")}
              >
                {busy ? <CircularProgress size={16} color="inherit" /> : copied ? <Check /> : <Copy />}
              </button>
            </span>
          </Tooltip>
          <Tooltip title={t("more")}>
            <button
              type="button"
              className={classes.iconBtn}
              onClick={(e) => setAnchor(e.currentTarget)}
              disabled={busy}
              aria-label={t("more")}
              aria-haspopup="menu"
            >
              <MoreHorizontal />
            </button>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            classes={{ paper: classes.menuPaper }}
          >
            <MenuItem onClick={pick(onPassword)}>
              <KeyRound />
              {share.passwordProtected ? t("changePassword") : t("setPassword")}
            </MenuItem>
            <MenuItem onClick={pick(onValidity)}>
              <CalendarClock />
              {t("changeValidity")}
            </MenuItem>
            <MenuItem onClick={pick(onRenew)}>
              <RefreshCw />
              {t("renew")}
            </MenuItem>
            <Divider style={{ margin: "4px 0" }} />
            <MenuItem onClick={pick(onRevoke)} className={classes.menuDanger}>
              <Ban />
              {t("revoke")}
            </MenuItem>
          </Menu>
        </div>
      )}
    </div>
  );
};

export default ShareRow;
